import { useEffect, useRef, useState } from "react";
import { useMobileAppLayout } from "@/lib/providers/mobile-app-layout-context";
import { cn } from "@/lib/utils";
import type { NavigationConfig } from "./types";

export function BaseNavigationBarMobile({
  title,
  backButton,
  leadElement,
  trailingElements,
  trailElement,
  bottomElement,
}: NavigationConfig) {
  // Combine trailElement and trailingElements for backward compatibility
  const allTrailingElements = [
    ...(trailElement ? [trailElement] : []),
    ...(trailingElements || []),
  ];
  const containerRef = useRef<HTMLDivElement>(null);
  const leadRef = useRef<HTMLDivElement>(null);
  const trailingRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const [showTitle, setShowTitle] = useState(true);

  // Use mobile app layout context
  const { isScrolled, setNavBarHeight } = useMobileAppLayout();

  // Measure and report nav bar height
  useEffect(() => {
    const measureHeight = () => {
      if (navRef.current) {
        const height = navRef.current.offsetHeight;
        setNavBarHeight(height);
      }
    };

    // Measure on mount and when content changes
    measureHeight();

    // Set up ResizeObserver to handle dynamic height changes
    const resizeObserver = new ResizeObserver(measureHeight);
    if (navRef.current) {
      resizeObserver.observe(navRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [setNavBarHeight]);

  useEffect(() => {
    const checkTitleVisibility = () => {
      if (!containerRef.current || !leadRef.current || !trailingRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const leadWidth = leadRef.current.offsetWidth;
      const trailingWidth = trailingRef.current.offsetWidth;
      const padding = 24; // 12px on each side (px-3)

      // Estimate title width (approximate based on text length)
      const titleWidth = title.length * 8 + 20; // rough estimation

      // Calculate available space for title
      const availableWidth = containerWidth - leadWidth - trailingWidth - padding * 2;

      // Hide title if there's not enough space (with some buffer)
      const shouldShowTitle = availableWidth > titleWidth + 60; // 60px buffer for earlier hiding
      setShowTitle(shouldShowTitle);
    };

    // Check on mount and when elements change
    checkTitleVisibility();

    // Add resize observer to handle dynamic changes
    const resizeObserver = new ResizeObserver(checkTitleVisibility);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [title]);
  return (
    <div className="md:hidden">
      <div
        ref={navRef}
        className={cn(
          "base-nav fixed left-0 right-0",
          "z-80 transition-all duration-200 ease-in-out",
          isScrolled && [
            "backdrop-blur-xl",
            "border-b border-border/70",
            "shadow-md shadow-black/10",
          ]
        )}
        style={{
          // Pushed down by the global paused-bookings banner when shown; the
          // banner reports its height without the safe area, which stays here.
          top: "var(--paused-banner-height, 0px)",
          paddingTop: "var(--safe-area-inset-top, 0px)",
        }}
      >
        {/* Top Navigation Bar */}
        <div ref={containerRef} className="relative flex items-center px-3 py-3 min-h-16">
          {/* Left Side - Back Button and Lead Element */}
          <div ref={leadRef} className="flex items-center gap-2">
            {backButton}
            {leadElement}
          </div>

          {/* Centered Title - conditionally rendered */}
          {showTitle && (
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <h1 className="text-lg font-semibold text-foreground whitespace-nowrap">{title}</h1>
            </div>
          )}

          {/* Right Side - Trailing Elements */}
          <div ref={trailingRef} className="flex items-center gap-2 ml-auto">
            {allTrailingElements.map((element, index) => (
              <div key={index}>{element}</div>
            ))}
          </div>
        </div>

        {/* Bottom Element */}
        {bottomElement && <div className="px-3 pb-3">{bottomElement}</div>}
      </div>
    </div>
  );
}
